import { Link, useLocation } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import { LockedNavTarget } from './LockedNavTarget'
import styles from './SectionSidebar.module.css'
import doneverseLogo from '@/assets/brand/doneverse-logo-blue.png'
import chevronRightDark from '@/assets/brand/icon-chevron-right-dark.svg'
import chevronRightWhite from '@/assets/brand/icon-chevron-right.svg'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useMobileSidebar } from '@/contexts/MobileSidebarContext'
import { useTourAnchors } from '@/contexts/TourAnchorContext'
import { useSlidingIndicator } from '@/hooks/useSlidingIndicator'
import type { TourAnchorId } from '@/lib/productTour'
import { matchesPathPrefix } from '@/lib/routes'
import { cn } from '@/lib/utils'

export interface SidebarNavItem {
  to?: string
  // External link — renders an <a> opening in a new tab instead of a router Link.
  href?: string
  label: string
  icon: React.ElementType
  panelImage?: string
  // Active on exact match only — for items whose route prefixes a sibling
  // item's route (e.g. Coaching Calls vs Call Replays).
  end?: boolean
  attention?: boolean
  // Reachable but not yet available — currently only the founder onboarding
  // lock. Rendered dimmed and unclickable, with `lockedReason` as the tooltip.
  // The item stays in the list on purpose: the founder should see what the rest
  // of the product holds while they finish getting set up.
  locked?: boolean
  lockedReason?: string
  /**
   * Stable anchor id for the founder product tour (lib/productTour), set in
   * founderNavConfig on the items the tour points at.
   *
   * Registered on the LOCKED rendering only, which is not a gap: the tour is
   * step 4's surface, step 4 is a pre-unlock step, and every sidebar item except
   * the founder home is locked for the whole of it. Were that ever to change,
   * the affected stop falls back to its rail anchor rather than breaking.
   */
  tourId?: TourAnchorId
}

interface SectionSidebarProps {
  navItems: SidebarNavItem[]
  showLogo?: boolean
  title?: string
}

export function SectionSidebar({ navItems, showLogo, title }: SectionSidebarProps) {
  const { pathname } = useLocation()
  const { mobileOpen, setMobileOpen } = useMobileSidebar()
  // Founder product tour anchors; a no-op outside the authenticated shell.
  const { register } = useTourAnchors()

  const isActive = (item: SidebarNavItem) => {
    if (!item.to) return false
    return item.end ? pathname === item.to : matchesPathPrefix(pathname, item.to)
  }

  const activeItem = navItems.find(isActive)
  const { containerRef, setItemRef, indicatorStyle } = useSlidingIndicator(activeItem?.to)

  const itemClass = (active: boolean) =>
    cn(
      'flex items-center gap-2 w-full rounded-[30px] p-[10px] transition-[color,background-color,transform] duration-200 ease-out active:scale-[0.96] text-sm font-semibold no-underline relative z-[1]',
      active ? 'text-white' : 'bg-transparent text-black hover:bg-surface-soft',
    )

  const PanelIcon = ({ item }: { item: SidebarNavItem }) => {
    if (item.panelImage) {
      return (
        <img
          src={item.panelImage}
          alt=""
          className="w-[34px] h-[34px] rounded-[17px] shrink-0 outline outline-1 -outline-offset-1 outline-black/10"
        />
      )
    }
    const Icon = item.icon
    return (
      <div className="w-[34px] h-[34px] rounded-full bg-[#D2FF92] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-black" strokeWidth={2.25} />
      </div>
    )
  }

  return (
    <div className={styles.sidebar} data-mobile-open={mobileOpen} data-testid="section-sidebar">
      <div className="px-5 pt-9 pb-3">
        {showLogo ? (
          <img src={doneverseLogo} alt="Doneverse" className="h-7" data-testid="sidebar-brand" />
        ) : title ? (
          <span className="text-lg font-bold text-foreground">{title}</span>
        ) : null}
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <nav className="px-5 py-4">
          <div ref={containerRef} className="relative flex flex-col gap-[10px]">
            {indicatorStyle && (
              <div
                className="absolute left-0 right-0 rounded-[30px] bg-doneverse-purple z-0"
                style={indicatorStyle}
              />
            )}
            {navItems.map((item) => {
              const active = isActive(item)
              const key = item.to ?? item.href ?? item.label
              const inner = (
                <>
                  <PanelIcon item={item} />
                  <span className="flex-1 min-w-0 truncate">{item.label}</span>
                  {item.attention && (
                    <span
                      className="h-2.5 w-2.5 rounded-full bg-red-500 shrink-0"
                      data-testid="sidebar-attention-dot"
                      aria-hidden="true"
                    />
                  )}
                  {item.href ? (
                    // External hand-off: the trailing chevron reads as "goes
                    // deeper in here", which this doesn't — it leaves for a new
                    // tab (e.g. Update Billing → Keap).
                    <>
                      <span className="sr-only">(opens in a new tab)</span>
                      <ExternalLink
                        className="w-4 h-4 shrink-0 mr-0.5"
                        strokeWidth={2.25}
                        aria-hidden="true"
                        data-testid="sidebar-external-icon"
                      />
                    </>
                  ) : (
                    <img
                      src={active ? chevronRightWhite : chevronRightDark}
                      alt=""
                      className="w-5 h-5 shrink-0"
                    />
                  )}
                </>
              )
              if (item.locked) {
                return (
                  <LockedNavTarget
                    key={key}
                    className={cn(itemClass(false), 'opacity-50 cursor-not-allowed')}
                    reason={item.lockedReason}
                    testId="sidebar-locked-item"
                    ref={register(item.tourId)}
                  >
                    {inner}
                  </LockedNavTarget>
                )
              }
              if (item.href) {
                return (
                  <a
                    key={key}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={itemClass(active)}
                    onClick={() => setMobileOpen(false)}
                  >
                    {inner}
                  </a>
                )
              }
              return (
                <Link
                  key={key}
                  to={item.to}
                  ref={(el) => setItemRef(item.to!, el)}
                  className={itemClass(active)}
                  onClick={() => setMobileOpen(false)}
                >
                  {inner}
                </Link>
              )
            })}
          </div>
        </nav>
      </ScrollArea>
    </div>
  )
}
